<script setup>
import { useMFK } from '@/composables/useMFK'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

// Module 16 (§9.3) — "reuse Master Data Equipment + ReminderBadge.vue
// kalibrasi" (blueprint §Module16 Vue Page). Halaman TERPISAH dari
// pages/master-data/equipments/index.vue (bukan CRUD lengkap — field dasar
// Equipment tetap murni Master Data), reuse GET /api/v1/master-data/
// equipments yang sama, tambahan kolom status kalibrasi + dialog edit
// kalibrasi (blueprint hanya menyebut SATU halaman untuk modul ini, tidak
// ada sub-halaman [id]/... seperti Module 15 HR — jadi dialog inline di
// sini, bukan route terpisah).
definePage({
  meta: {
    layout: 'default',
    action: 'equipment-manage',
    subject: 'Mfk',
  },
})

const ability = useAbility()
const { saving, updateCalibration } = useMFK()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/equipments', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = response?.meta?.total ?? data?.meta?.total ?? 0
  } catch (e) {
    console.error('Fetch equipments error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const dialog = ref(false)
const editingItem = ref(null)
const calibrationDate = ref('')
const saveError = ref('')

const openCalibration = item => {
  editingItem.value = item
  calibrationDate.value = item.calibration_due_date ?? ''
  saveError.value = ''
  dialog.value = true
}

const saveCalibration = async () => {
  saveError.value = ''
  try {
    const updated = await updateCalibration(editingItem.value.id, {
      calibration_due_date: calibrationDate.value || null,
    })

    const idx = items.value.findIndex(i => i.id === editingItem.value.id)
    if (idx !== -1) items.value[idx] = updated
    dialog.value = false
  } catch (e) {
    saveError.value = e?.data?.message ?? 'Gagal menyimpan tanggal kalibrasi.'
  }
}

const headers = [
  { title: 'Kode Aset', key: 'code', width: 130 },
  { title: 'Nama', key: 'name' },
  { title: 'Unit', key: 'unit' },
  { title: 'Kalibrasi', key: 'calibration', align: 'center' },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-bold">
          Kalibrasi Alat
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Status & reminder kalibrasi peralatan (§9.3)
        </p>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari nama atau kode aset..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.calibration="{ item }">
          <div class="d-flex align-center justify-center gap-2">
            <span
              v-if="item.calibration_due_date"
              class="text-body-2"
            >{{ item.calibration_due_date }}</span>
            <span
              v-else
              class="text-medium-emphasis text-body-2"
            >Belum diisi</span>
            <ReminderBadge
              remindable-type="equipment"
              :remindable-id="item.id"
            />
          </div>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('equipment-manage', 'Mfk')"
            icon="ri-calendar-check-line"
            variant="text"
            size="small"
            @click="openCalibration(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data alat
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="480"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Kalibrasi — {{ editingItem?.name }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VAlert
            v-if="saveError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ saveError }}
          </VAlert>
          <VTextField
            v-model="calibrationDate"
            label="Tanggal Jatuh Tempo Kalibrasi Berikutnya"
            type="date"
          />
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveCalibration"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
